/**
 * Tenant Service for Whitelabel Multi-Tenant Architecture
 *
 * This service handles:
 * - Origin detection and tenant ID resolution
 * - Tenant configuration fetching from BFF
 * - LocalStorage caching (24h TTL)
 * - Branding application
 */

import axiosInstance from './api/axiosInstance';
import { detectOrigin } from '../utils/originDetection';
import { applyBranding, DEFAULT_BRANDING } from '../utils/applyBranding';
import type { TenantConfig, TenantResolutionResponse, CachedTenantConfig } from '../types/tenant';
import { logger } from '../utils/logger';

const CACHE_KEY_PREFIX = 'claim_portal:tenant_config:';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Cache schema version — part of the key, so a config cached under an older schema is a cache
 * MISS rather than a config silently missing its newest field (DEV-7682).
 *
 * The TTL is 24 h: without this, every browser holding a warm cache at deploy time would read
 * `features.requireEventDate === undefined` for a full day and keep sourcing beneficiaries from
 * the Parent Context on a tenant that just switched to the event-date flow. Bump it whenever the
 * app starts READING a new `TenantConfig` field.
 *
 * Entries left under an older version are simply never read again; `clearTenantConfigCache()`
 * with no tenant still sweeps them, since it matches on the un-versioned prefix.
 *
 * `v2` → `v3` (DEV-7728): `03B-01-DETAILS` now reads `baggageDelayNotCoveredUpToHours`.
 * `v3` → `v4` (DEV-7491): the beneficiary screens now read `beneficiariesFromEligibility`.
 * `v4` → `v5` (DEV-7932): the footer, the maintenance page and the contact error sentences now
 * read `displaySupportContact`. Without the bump, every returning browser would keep serving a
 * cached config where the field is `undefined` for up to 24 h and — the read failing CLOSED — would
 * strip the assistance line from the very insurer that is supposed to keep it.
 */
const CACHE_SCHEMA_VERSION = 'v5';

const buildCacheKey = (tenantId: string): string =>
  `${CACHE_KEY_PREFIX}${CACHE_SCHEMA_VERSION}:${tenantId}`;

/**
 * Initializes tenant configuration based on origin detection or explicit tenant ID.
 *
 * This function orchestrates the complete whitelabel initialization flow:
 * 1. If contextTenantId provided → Use it directly (skip origin detection)
 * 2. Otherwise: Detect iframe parent origin (window.parent or PostMessage)
 * 3. Check localStorage cache for tenant config
 * 4. If cache valid, apply branding and return cached config
 * 5. If cache invalid/missing, resolve tenant ID from BFF (if no contextTenantId)
 * 6. Fetch tenant config from BFF
 * 7. Cache config in localStorage (24h TTL)
 * 8. Apply branding dynamically (CSS variables)
 * 9. Return tenant config
 *
 * Fallback Behavior:
 * - If origin detection fails → default branding, no tenant
 * - If BFF calls fail → default branding, no tenant
 * - If timeout (>5s) → default branding, no tenant
 *
 * Example Usage:
 * ```typescript
 * // In EmbeddedAppProvider - via origin detection
 * const config = await initializeTenantConfig();
 *
 * // In EmbeddedAppProvider - via context tenantId
 * const config = await initializeTenantConfig('BLUE_CROSS_INSURANCE');
 * ```
 *
 * @param contextTenantId - Optional: Tenant ID provided by parent context (overrides origin detection)
 * @returns Promise<TenantConfig> - Tenant configuration (never null, falls back to default branding)
 */
export async function initializeTenantConfig(contextTenantId?: string): Promise<TenantConfig> {
  try {
    let tenantId: string;

    // Step 1: Determine tenant ID (from context or origin detection)
    if (contextTenantId) {
      // Parent context explicitly provided tenant ID
      tenantId = contextTenantId;
    } else {
      // Step 1a: Detect iframe parent origin
      const origin = await detectOrigin();

      if (!origin) {
        return getDefaultBrandingConfig();
      }

      // Step 1b: Resolve tenant ID from origin
      const resolutionResponse = await resolveTenantIdFromOrigin(origin);
      tenantId = resolutionResponse.tenantId;
    }

    // Step 2: Check cache before fetching
    const cachedConfig = getCachedTenantConfig(tenantId);
    if (cachedConfig) {
      applyBranding(cachedConfig.branding);
      return cachedConfig;
    }

    // Step 3: Fetch tenant config from BFF
    const config = await fetchTenantConfig(tenantId);

    // Step 4: Cache config in localStorage
    cacheTenantConfig(tenantId, config);

    // Step 5: Apply branding dynamically
    applyBranding(config.branding);

    console.info('[TenantService] Tenant configuration initialized:', tenantId);
    return config;

  } catch (error) {
    logger.error('Failed to initialize tenant config, falling back to default branding', {
      errorType: 'TenantConfigError',
      service: 'TenantService'
    }, error as Error);
    return getDefaultBrandingConfig();
  }
}

/**
 * Resolves tenant ID from origin URL via BFF API.
 *
 * Calls GET /api/tenants/resolve-origin?origin=https://app.croixbleue.ca
 *
 * IMPORTANT: This endpoint requires JWT authentication and X-Tenant-ID header.
 * Uses axiosInstance which automatically injects these headers via interceptors.
 *
 * @param origin - Iframe parent origin URL
 * @returns Promise<TenantResolutionResponse> - Tenant resolution response
 */
async function resolveTenantIdFromOrigin(origin: string): Promise<TenantResolutionResponse> {
  try {
    const response = await axiosInstance.get<TenantResolutionResponse>('/api/tenants/resolve-origin', {
      params: { origin },
    });

    return response.data;
  } catch (error) {
    logger.error('Failed to resolve tenant ID from origin', {
      errorType: 'TenantResolutionError',
      service: 'TenantService',
      origin
    }, error as Error);
    // No tenant is invented here: the BFF answers 404 for an origin its whitelist does not name,
    // and naming one anyway would send every later call under a tenant this origin has no claim to.
    // The caller falls back to default branding, which needs no tenant.
    throw error;
  }
}

/**
 * Fetches tenant configuration from BFF API.
 *
 * Calls GET /api/tenants/{tenantId}/config
 *
 * IMPORTANT: This endpoint requires JWT authentication and X-Tenant-ID header.
 * Uses axiosInstance which automatically injects these headers via interceptors.
 *
 * @param tenantId - Tenant identifier
 * @returns Promise<TenantConfig> - Complete tenant configuration
 */
async function fetchTenantConfig(tenantId: string): Promise<TenantConfig> {
  try {
    const response = await axiosInstance.get<TenantConfig>(`/api/tenants/${tenantId}/config`);
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch tenant config', {
      errorType: 'TenantConfigFetchError',
      service: 'TenantService',
      tenantId
    }, error as Error);
    return getDefaultBrandingConfig();
  }
}

/**
 * Gets cached tenant configuration from localStorage.
 *
 * Cache Validation:
 * - Check if cached config exists for tenant ID
 * - Check if cache is not expired (cachedAt + TTL > now)
 * - Return null if cache invalid or missing
 *
 * @param tenantId - Tenant identifier
 * @returns TenantConfig | null - Cached config or null if invalid/missing
 */
function getCachedTenantConfig(tenantId: string): TenantConfig | null {
  try {
    const cacheKey = buildCacheKey(tenantId);
    const cachedJson = localStorage.getItem(cacheKey);

    if (!cachedJson) {
      return null;
    }

    const cached: CachedTenantConfig = JSON.parse(cachedJson);

    // Check if cache is expired
    const now = Date.now();
    const expiresAt = cached.cachedAt + cached.ttl;

    if (now > expiresAt) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    return cached.config;

  } catch (error) {
    logger.error('Failed to read cached tenant config', {
      errorType: 'TenantCacheReadError',
      service: 'TenantService',
      tenantId
    }, error as Error);
    return null;
  }
}

/**
 * Caches tenant configuration in localStorage.
 *
 * Cache Structure:
 * ```json
 * {
 *   "config": { ... },
 *   "cachedAt": 1730123456789,
 *   "ttl": 86400000
 * }
 * ```
 *
 * @param tenantId - Tenant identifier
 * @param config - Tenant configuration to cache
 */
function cacheTenantConfig(tenantId: string, config: TenantConfig): void {
  try {
    const cacheKey = buildCacheKey(tenantId);
    const cached: CachedTenantConfig = {
      config,
      cachedAt: Date.now(),
      ttl: CACHE_TTL_MS,
    };

    localStorage.setItem(cacheKey, JSON.stringify(cached));

  } catch (error) {
    logger.error('Failed to cache tenant config', {
      errorType: 'TenantCacheWriteError',
      service: 'TenantService',
      tenantId
    }, error as Error);
  }
}

/**
 * Clears cached tenant configuration from localStorage.
 *
 * Use cases:
 * - Origin change detected (user navigated to different tenant)
 * - Manual cache invalidation
 *
 * @param tenantId - Tenant identifier (optional, clears all if not provided)
 */
export function clearTenantConfigCache(tenantId?: string): void {
  try {
    if (tenantId) {
      localStorage.removeItem(buildCacheKey(tenantId));
    } else {
      // Clear all tenant config caches
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith(CACHE_KEY_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    }
  } catch (error) {
    logger.error('Failed to clear tenant config cache', {
      errorType: 'TenantCacheClearError',
      service: 'TenantService',
      tenantId
    }, error as Error);
  }
}

/**
 * Gets the default branding configuration.
 *
 * This configuration is used as fallback when:
 * - Origin detection fails (timeout, error)
 * - Origin not recognized (not in whitelist)
 * - Standalone mode (no iframe embedding)
 * - BFF API calls fail
 *
 * Carries no tenant id: it is the default look, not a tenant anyone authenticates as.
 *
 * @returns TenantConfig - Default branding, with no tenant claimed
 */
function getDefaultBrandingConfig(): TenantConfig {
  const defaultConfig: TenantConfig = {
    tenantId: '',
    branding: DEFAULT_BRANDING,
    features: {
      enableRTA: true,
      supportedLanguages: ['fr', 'en'],
      // DEV-7682 — deliberately `true`, i.e. the DEFAULT FAILS CLOSED.
      //
      // This object is returned whenever the config could not be read: origin not resolved,
      // /config down, timeout. `false` would then be a silent, invisible regression — the
      // beneficiary screens would go back to listing Parent Context people, none of them
      // filtered on the event date, with nothing on screen saying so. `true` can only ever
      // WITHHOLD names: the eligibility list is used, and if it is empty the claimant gets
      // `steps.beneficiary.noBeneficiariesMessage`, which points to customer service. A
      // visible dead end beats a plausible wrong list on an insurance claim.
      //
      // Trade-off accepted: a tenant that does NOT scope claims by event date (PV) also gets
      // the eligibility-only path while its config is unreachable. That degrades to the same
      // visible empty state, and only for the duration of the outage.
      requireEventDate: true,
      // DEV-7491 — fails closed like requireEventDate above, and for the same reason: with
      // no config in hand, the beneficiary screens read the eligibility response, which can
      // only WITHHOLD names (an empty list shows noBeneficiariesMessage). `false` would
      // silently list Parent Context people that a shell-served tenant never has — or, on
      // an event-date tenant, people never filtered on the incident date.
      beneficiariesFromEligibility: true,
      // DEV-7728 — fails closed for the same reason. Absent here would fail OPEN: a Croix
      // Bleue claimant whose /config call did not answer could file an uncovered 3 h delay
      // with nothing saying so. Too strict is a visible dead end; too lax is invisible.
      baggageDelayNotCoveredUpToHours: 12,
      // DEV-7932 — fails closed, like the three flags above. With no config resolved we do not
      // know WHOSE members are on screen, and the assistance line belongs to one insurer only:
      // `true` here would print it to another insurer's members, who would call a service that
      // cannot open their file. Hiding it leaves the claimant with the contact details of the
      // portal that sent them here; publishing the wrong claims line cannot be taken back.
      displaySupportContact: false,
    },
    integrations: {
      salesforceOrgId: '',
      sharePointSiteId: '',
    },
  };

  // Apply default branding
  applyBranding(defaultConfig.branding);

  return defaultConfig;
}
